import type { HandAttachPose } from './ProcArms';
import { bakedAttachPose } from './ProcArms';

export type DoubleReloadBeatId = 'closed' | 'open' | 'pocket' | 'hold' | 'press' | 'clear' | 'shut';

/** Pose-local offset: +pitch = muzzle up, +yaw = nose left, +roll = clockwise. */
export interface ReloadXform {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
}

export interface ReloadPairXform {
  x: number;
  y: number;
  z: number;
  rx: number;
  ry: number;
  rz: number;
}

export interface DoubleReloadBeat {
  id: DoubleReloadBeatId;
  label: string;
  /** 0..1 of reloadTime. */
  t: number;
  gun: ReloadXform;
  /** barrelBreak.rotation.x (negative drops the muzzles). */
  breakX: number;
  /** 0 = left hand on grip_l, 1 = left hand on dummy. */
  dummyBlend: number;
  /**
   * 1 = dummy sits on the forend grip (hip hand orientation, xyz is a pose offset).
   * 0 = dummy uses its own pitch/yaw/roll in pose space.
   */
  followGrip: number;
  dummy: ReloadXform;
  hand: HandAttachPose;
  pair: ReloadPairXform;
  pairVis: number;
  chamberVis: number;
}

const ZERO: ReloadXform = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 };
const PAIR: ReloadPairXform = { x: 0, y: -0.016, z: -0.038, rx: 0, ry: 0, rz: 0 };

function hipL(): HandAttachPose {
  return bakedAttachPose('sawedoff', 'L');
}

function xform(src: ReloadXform): ReloadXform {
  return { ...src };
}

function pairOf(src: ReloadPairXform): ReloadPairXform {
  return { ...src };
}

function beat(
  id: DoubleReloadBeatId,
  label: string,
  t: number,
  extra: Omit<DoubleReloadBeat, 'id' | 'label' | 't'>,
): DoubleReloadBeat {
  return {
    id,
    label,
    t,
    gun: xform(extra.gun),
    breakX: extra.breakX,
    dummyBlend: extra.dummyBlend,
    followGrip: extra.followGrip,
    dummy: xform(extra.dummy),
    hand: { ...extra.hand },
    pair: pairOf(extra.pair),
    pairVis: extra.pairVis,
    chamberVis: extra.chamberVis,
  };
}

export const DOUBLE_RELOAD_BEAT_ORDER: DoubleReloadBeatId[] = [
  'closed',
  'open',
  'pocket',
  'hold',
  'press',
  'clear',
  'shut',
];

export function bakedReloadClip(): DoubleReloadBeat[] {
  return [
    beat('closed', 'Closed', 0, {
      gun: ZERO,
      breakX: 0,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: hipL(),
      pair: PAIR,
      pairVis: 0,
      chamberVis: 1,
    }),
    beat('open', 'Break open', 0.1, {
      gun: { x: -0.041, y: 0.058, z: 0.14, pitch: 0.8, yaw: 0.53, roll: -0.11 },
      breakX: -1.4,
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.123, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 },
      hand: {
        x: -0.118,
        y: -0.045,
        z: 0.063,
        rx: 0.32,
        ry: -0.43,
        rz: 0.1,
        wx: -0.54,
        wy: -0.54,
        wz: -2.44,
        scale: 1.3,
        curl: -0.15,
        knuckle: -0.29,
      },
      pair: PAIR,
      pairVis: 0,
      chamberVis: 0,
    }),
    beat('pocket', 'Pocket', 0.17, {
      gun: { x: -0.051, y: 0.065, z: 0.126, pitch: 0.8, yaw: 0.53, roll: -0.11 },
      breakX: -1.4,
      dummyBlend: 0.56,
      followGrip: 1,
      dummy: { x: -0.4, y: -0.36, z: -0.027, pitch: -1.36, yaw: 0.12, roll: 0.08 },
      hand: {
        x: 0,
        y: 0.005,
        z: 0.01,
        rx: 0.32,
        ry: -0.43,
        rz: 0.1,
        wx: 0.22,
        wy: 0,
        wz: 0,
        scale: 1.28,
        curl: 0.48,
        knuckle: 0.22,
      },
      pair: { x: 0, y: 0.054, z: -0.038, rx: 0, ry: 0, rz: 0 },
      pairVis: 1,
      chamberVis: 0,
    }),
    beat('hold', 'Hold shells', 0.22, {
      gun: { x: -0.051, y: 0.065, z: 0.126, pitch: 0.8, yaw: 0.53, roll: -0.11 },
      breakX: -1.4,
      dummyBlend: 1,
      followGrip: 0,
      dummy: { x: -0.068, y: -0.02, z: 0.07, pitch: 0.38, yaw: 0.06, roll: 0.04 },
      hand: {
        x: -0.09,
        y: 0.025,
        z: -0.02,
        rx: 0.36,
        ry: -0.83,
        rz: 1.08,
        wx: 0.14,
        wy: 1.11,
        wz: 0.86,
        scale: 1.27,
        curl: -0.76,
        knuckle: 0.22,
      },
      pair: { x: -0.062, y: 0.014, z: -0.041, rx: -1.15, ry: -0.07, rz: 0 },
      pairVis: 1,
      chamberVis: 0,
    }),
    beat('press', 'Press in', 0.4, {
      gun: { x: -0.1, y: 0.055, z: 0.03, pitch: 0.36, yaw: 0.22, roll: 0.07 },
      breakX: -1.4,
      dummyBlend: 1,
      followGrip: 0,
      dummy: { x: 0, y: -0.008, z: 0.055, pitch: 0.21, yaw: 0, roll: 0.11 },
      hand: {
        x: -0.023,
        y: 0.045,
        z: 0.013,
        rx: -0.11,
        ry: -0.39,
        rz: 0.07,
        wx: 0.57,
        wy: -0.04,
        wz: 0.25,
        scale: 1.28,
        curl: -0.4,
        knuckle: 0.22,
      },
      pair: { x: 0.008, y: 0.023, z: -0.009, rx: -1.33, ry: 0, rz: 0 },
      pairVis: 1,
      chamberVis: 0,
    }),
    beat('clear', 'Clear left', 0.49, {
      gun: { x: -0.08, y: 0.044, z: 0.024, pitch: 0.29, yaw: 0.18, roll: 0.056 },
      breakX: -1.4,
      dummyBlend: 1,
      followGrip: 0,
      dummy: { x: -0.16, y: 0.02, z: 0.055, pitch: 0.12, yaw: 0.28, roll: 0.05 },
      hand: {
        x: 0.085,
        y: -0.013,
        z: 0.028,
        rx: -0.65,
        ry: -0.86,
        rz: -1.04,
        wx: -0.32,
        wy: 0.14,
        wz: 2.47,
        scale: 1.29,
        curl: -0.21,
        knuckle: -0.11,
      },
      pair: { x: 0.009, y: -0.01, z: -0.038, rx: -0.07, ry: 0, rz: 0.25 },
      pairVis: 0,
      chamberVis: 1,
    }),
    beat('shut', 'Snap shut', 0.64, {
      gun: ZERO,
      breakX: 0,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: hipL(),
      pair: PAIR,
      pairVis: 0,
      chamberVis: 1,
    }),
  ];
}

export function cloneReloadClip(src: DoubleReloadBeat[] = bakedReloadClip()): DoubleReloadBeat[] {
  return src.map((b) => beat(b.id, b.label, b.t, b));
}

export function copyReloadBeat(src: DoubleReloadBeat, dst: DoubleReloadBeat): void {
  dst.id = src.id;
  dst.label = src.label;
  dst.t = src.t;
  Object.assign(dst.gun, src.gun);
  dst.breakX = src.breakX;
  dst.dummyBlend = src.dummyBlend;
  dst.followGrip = src.followGrip;
  Object.assign(dst.dummy, src.dummy);
  Object.assign(dst.hand, src.hand);
  Object.assign(dst.pair, src.pair);
  dst.pairVis = src.pairVis;
  dst.chamberVis = src.chamberVis;
}

export function emptyReloadSample(): DoubleReloadBeat {
  return beat('closed', 'Closed', 0, {
    gun: ZERO,
    breakX: 0,
    dummyBlend: 0,
    followGrip: 1,
    dummy: ZERO,
    hand: hipL(),
    pair: PAIR,
    pairVis: 0,
    chamberVis: 1,
  });
}

function lerpNum(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpXform(a: ReloadXform, b: ReloadXform, t: number, out: ReloadXform): void {
  out.x = lerpNum(a.x, b.x, t);
  out.y = lerpNum(a.y, b.y, t);
  out.z = lerpNum(a.z, b.z, t);
  out.pitch = lerpNum(a.pitch, b.pitch, t);
  out.yaw = lerpNum(a.yaw, b.yaw, t);
  out.roll = lerpNum(a.roll, b.roll, t);
}

function lerpHand(a: HandAttachPose, b: HandAttachPose, t: number, out: HandAttachPose): void {
  out.x = lerpNum(a.x, b.x, t);
  out.y = lerpNum(a.y, b.y, t);
  out.z = lerpNum(a.z, b.z, t);
  out.rx = lerpNum(a.rx, b.rx, t);
  out.ry = lerpNum(a.ry, b.ry, t);
  out.rz = lerpNum(a.rz, b.rz, t);
  out.wx = lerpNum(a.wx, b.wx, t);
  out.wy = lerpNum(a.wy, b.wy, t);
  out.wz = lerpNum(a.wz, b.wz, t);
  out.scale = lerpNum(a.scale, b.scale, t);
  out.curl = lerpNum(a.curl, b.curl, t);
  out.knuckle = lerpNum(a.knuckle, b.knuckle, t);
}

function lerpPair(a: ReloadPairXform, b: ReloadPairXform, t: number, out: ReloadPairXform): void {
  out.x = lerpNum(a.x, b.x, t);
  out.y = lerpNum(a.y, b.y, t);
  out.z = lerpNum(a.z, b.z, t);
  out.rx = lerpNum(a.rx, b.rx, t);
  out.ry = lerpNum(a.ry, b.ry, t);
  out.rz = lerpNum(a.rz, b.rz, t);
}

function smooth01(t: number): number {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  return u * u * (3 - 2 * u);
}

/** Interpolate the clip at t (0..1). Writes into `out`. */
export function sampleReloadClip(clip: DoubleReloadBeat[], t: number, out: DoubleReloadBeat): DoubleReloadBeat {
  const n = clip.length;
  if (n === 0) return out;
  const first = clip[0]!;
  const last = clip[n - 1]!;
  if (t <= first.t) {
    copyReloadBeat(first, out);
    return out;
  }
  if (t >= last.t) {
    copyReloadBeat(last, out);
    return out;
  }
  let i = 0;
  while (i < n - 1 && clip[i + 1]!.t < t) i += 1;
  const a = clip[i]!;
  const b = clip[i + 1]!;
  const span = b.t - a.t;
  const u = span <= 1e-6 ? 1 : smooth01((t - a.t) / span);
  out.id = u < 1 ? a.id : b.id;
  out.label = u < 1 ? a.label : b.label;
  out.t = t;
  lerpXform(a.gun, b.gun, u, out.gun);
  out.breakX = lerpNum(a.breakX, b.breakX, u);
  out.dummyBlend = lerpNum(a.dummyBlend, b.dummyBlend, u);
  out.followGrip = lerpNum(a.followGrip, b.followGrip, u);
  lerpXform(a.dummy, b.dummy, u, out.dummy);
  lerpHand(a.hand, b.hand, u, out.hand);
  lerpPair(a.pair, b.pair, u, out.pair);
  out.pairVis = lerpNum(a.pairVis, b.pairVis, u);
  out.chamberVis = lerpNum(a.chamberVis, b.chamberVis, u);
  return out;
}
